import { Link } from "@frontend/lib/link";
import { BoostTierIcon } from "@frontend/components/boost-tier-icon";
import { CallHeaderButton } from "@frontend/components/collab/call-header-button";
import { CollabActionBox } from "@frontend/components/collab/collab-action-box";
import { CollabHeaderMenu } from "@frontend/components/collab/collab-header-menu";
import { CollabRoomFrame } from "@frontend/components/collab/collab-mobile-room";
import { CollabRail } from "@frontend/components/collab/collab-rail";
import { CollabRoomSync } from "@frontend/components/collab/collab-room-sync";
import {
  CancelCollabCard,
  SharedFilesCard,
  SharedLinksCard,
} from "@frontend/components/collab/details-sections";
import { OfferCard } from "@frontend/components/collab/offer-card";
import { PaymentsPanel } from "@frontend/components/collab/payments-panel";
import { Thread } from "@frontend/components/collab/thread";
import { TrackScoreBadge } from "@frontend/components/track-score-badge";
import { Avatar, Callout, Money, Pill } from "@frontend/components/ui";
import { ROLE_LABELS, roleCan } from "@creators/backend/lib/access/roles";
import type { CollabAccess } from "@creators/backend/lib/auth";
import type { CollabListRow, CollabRoom } from "@creators/backend/lib/collabs/queries";
import {
  effectiveCollabStatus,
  isTerminal,
  statusLine,
  statusPill,
  type CollabStatus,
} from "@creators/backend/lib/collabs/state-machine";
import { formatDate } from "@creators/backend/lib/time";
import { PageSkeleton } from "@frontend/components/page-skeleton";
import { clientApi } from "@frontend/lib/api";
import { collabsCacheKey } from "@frontend/lib/data-cache";
import { useAsync } from "@frontend/lib/use-async";
import { useViewer } from "@frontend/lib/viewer";
import { useEffect } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { accountAvatarUrl, creatorAvatarUrl } from "@frontend/lib/avatar";
import { roomNeedsYou } from "@frontend/lib/collab-tabs";
import { listCounterpartTrack } from "@frontend/lib/reliability";
import { branding } from "@creators/backend/lib/config";

export function CollabRoomPage() {
  const { id = "" } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { viewer: sessionViewer } = useViewer();
  useEffect(() => { document.title = `Collab room · ${branding.name}`; }, []);
  const tabParam = searchParams.get("tab");
  const initialTab = tabParam === "details" || tabParam === "chat" ? tabParam : null;

  const inbox = useAsync(
    async () => {
      if (!sessionViewer || sessionViewer.isAdmin) return [] as CollabListRow[];
      const { collabs } = await clientApi<{ collabs: CollabListRow[] }>("/api/collabs").catch(
        () => ({ collabs: [] as CollabListRow[] }),
      );
      return collabs;
    },
    [sessionViewer?.sessionId, sessionViewer?.isAdmin, sessionViewer?.creator?.id],
    {
      cacheKey: collabsCacheKey(sessionViewer?.sessionId, sessionViewer?.creator?.id),
    },
  );

  const state = useAsync(
    async () => {
      if (!/^[0-9a-f-]{36}$/i.test(id)) return null;
      // Let network errors throw so useAsync keeps the last good room painted.
      const payload = await clientApi<{ room: CollabRoom; access: CollabAccess }>(
        `/api/collabs/${id}`,
      );
      if (!payload?.access) return null;
      return payload;
    },
    [id],
    { cacheKey: /^[0-9a-f-]{36}$/i.test(id) ? `collab-room:${id}` : null },
  );

  // Only the first load shows a skeleton — never again once we have a room.
  if (!state.data) {
    if (state.status === "loading" || state.status === "error") {
      return <PageSkeleton variant="inbox" />;
    }
    return <Navigate to="/collabs" replace />;
  }

  const { room, access } = state.data;
  const collabs = inbox.data ?? [];

  const viewer = access.side;
  const rawStatus = room.collab.status as CollabStatus;
  const status = effectiveCollabStatus(rawStatus, room.milestones);
  const brandName = room.brand.companyName || room.brand.name || room.brand.phone;
  const counterpart = viewer === "creator" ? brandName : room.creator.displayName;
  const counterpartAvatar =
    viewer === "creator"
      ? accountAvatarUrl(room.brandAccount)
      : creatorAvatarUrl(room.creator.id, room.creator.avatarKey);
  const paymentDue = room.milestones.some((m) => m.status === "due");
  const paymentAwaitingConfirm = room.milestones.some((m) => m.status === "paid");
  const pill = statusPill(status, paymentDue, paymentAwaitingConfirm);
  const timer = room.activeTimer;
  const needsAction = roomNeedsYou(room, viewer);
  const finished = isTerminal(status);
  const canPost = access.canChat && !finished;
  // A team member reaches the room through the creator's account, so what they can
  // do here comes from their role rather than from which side they are on.
  const delegated = access.role !== null && access.role !== "owner";
  const canManagePayouts = access.role ? roleCan(access.role, "payouts:manage") : true;
  const offer = room.currentOffer;
  const canRespondToOffer =
    Boolean(offer) &&
    access.canAct &&
    (status === "offer_pending" || status === "negotiating") &&
    offer!.createdBy !== viewer;
  // Basic opening offers stay quiet in the inbox, but Details still needs a badge.
  const detailsBadge = needsAction || canRespondToOffer;
  // Anyone signed in gets the inbox beside the thread; a brand reading the collab
  // from a share link has no list, so for them the room stays two panes.
  const showRail = collabs.length > 0;
  const line =
    status === "completed"
      ? null
      : statusLine(
          status,
          room.collab.pendingParty,
          viewer === "admin" ? "creator" : viewer,
          paymentDue,
          paymentAwaitingConfirm,
        );

  const track =
    viewer === "admin"
      ? null
      : listCounterpartTrack({
          yourRole: viewer,
          payer: room.trust.payer,
          delivery: room.trust.delivery,
        });

  const notices = [
    viewer === "brand" && !access.verified ? (
      <Callout key="unverified" tone="blue" title="You are viewing this with the link">
        <Link
          href={`/login?next=${encodeURIComponent(`/collabs/${id}`)}`}
          className="underline underline-offset-2"
        >
          Sign in with WhatsApp
        </Link>{" "}
        when you are ready to accept or pay.
      </Callout>
    ) : null,
    delegated && !access.canAct ? (
      <Callout
        key="delegated"
        tone="amber"
        title={`You are a ${ROLE_LABELS[access.role!]} on ${room.creator.displayName}'s account`}
      >
        You can read this collab but not act on it or post in the thread.
      </Callout>
    ) : null,
  ].filter(Boolean);

  return (
    <div className="mx-auto flex h-full min-h-0 w-full max-w-6xl flex-1 flex-col px-3 py-2 sm:px-4 sm:py-3 lg:py-4">
      <CollabRoomSync collabId={id} revision={room.revision} onChange={state.reload} />

      <CollabRoomFrame
        needsAction={detailsBadge}
        preferDetails={finished}
        initialTab={initialTab}
        rail={
          showRail ? (
            <div className="hidden shrink-0 border-r border-line lg:block lg:w-[17rem]">
              <CollabRail collabs={collabs} activeId={id} />
            </div>
          ) : null
        }
        header={
          <header className="flex shrink-0 items-center gap-2 border-b border-line px-2.5 py-2 sm:gap-3 sm:px-4 lg:px-6">
            <Link
              href="/collabs"
              className={`-ml-0.5 inline-flex size-9 shrink-0 items-center justify-center rounded-lg text-ink-subtle transition hover:bg-canvas hover:text-ink sm:size-10 ${showRail ? "lg:hidden" : ""}`}
            >
              <span className="sr-only">Back to collabs</span>
              <BackIcon />
            </Link>

            <Avatar
              name={counterpart}
              url={counterpartAvatar}
              size="sm"
              className={`sm:size-9 sm:text-xs ${showRail ? "lg:hidden" : ""}`}
            />

            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 items-center gap-1.5">
                <h1 className="truncate text-sm font-semibold tracking-tight text-ink sm:text-base">
                  {counterpart}
                </h1>
                <BoostTierIcon tier={room.collab.boostTier} className="size-4" />
                {track?.value ? (
                  <TrackScoreBadge
                    value={track.value}
                    tone={track.tone}
                    title={`${track.label} · ${track.detail}`}
                  />
                ) : null}
              </div>
              <p className="truncate text-[11px] text-ink-subtle sm:text-xs">
                <span className="tabular">{room.collab.reference}</span>
                {line ? (
                  <>
                    <span aria-hidden> · </span>
                    <span>{line}</span>
                  </>
                ) : null}
              </p>
            </div>

            <div className="flex max-w-[36%] shrink-0 flex-col items-end gap-0.5 sm:max-w-none">
              {room.currentOffer && room.currentOffer.compensationType !== "barter" ? (
                <Money
                  paise={room.currentOffer.amountTotal}
                  size="sm"
                  className="text-xs text-ink sm:text-sm"
                />
              ) : null}
              <Pill tone={pill.tone} size="sm" className="sm:text-xs sm:leading-normal">
                {pill.label}
              </Pill>
            </div>

            {room.collab.boostTier === "priority" &&
            room.calls &&
            (viewer === "creator" || viewer === "brand") ? (
              <CallHeaderButton
                collabId={id}
                viewer={viewer}
                calls={room.calls}
                canAct={canPost}
                viewerPhone={viewer === "creator" ? room.creator.phone : room.brand.phone}
              />
            ) : null}

            {viewer === "creator" || viewer === "brand" ? (
              <CollabHeaderMenu
                collabId={id}
                counterpartName={counterpart}
                muted={Boolean(room.muted)}
                blocked={Boolean(room.blocked)}
                onViewProfile={
                  viewer === "brand" && room.creator.handle
                    ? () => navigate(`/${room.creator.handle}`)
                    : viewer === "creator" && room.brandAccount?.handle
                      ? () => navigate(`/${room.brandAccount!.handle}`)
                      : undefined
                }
              />
            ) : null}
          </header>
        }
        chat={
          <>
            {notices.length > 0 ? (
              <div className="shrink-0 space-y-2 border-b border-line px-3 py-2.5 sm:px-4 lg:px-6">
                {notices}
              </div>
            ) : null}
            <Thread
              collabId={id}
              messages={room.messages}
              viewer={viewer}
              creatorName={room.creator.displayName}
              brandName={brandName}
              offerHistory={room.offerHistory}
              acceptedBy={room.terms?.acceptedBy ?? null}
              canPost={canPost}
              closedReason={finished ? "Chat closed" : "View only — you can’t post here"}
              showReadReceipts={room.collab.boostTier === "priority"}
              counterpartLastReadAt={room.counterpartLastReadAt}
              callRequestPending={Boolean(room.calls?.requested)}
            />
          </>
        }
        details={
          <div className="pb-10">
            <CollabActionBox
              room={room}
              viewer={viewer}
              verified={access.verified}
              canAct={access.canAct}
              timer={timer}
            />
            <div className="space-y-3 p-3 lg:pb-8">
              {room.currentOffer ? (
                <OfferCard
                  offer={room.currentOffer}
                  changes={room.changes}
                  status={status}
                  pendingParty={room.collab.pendingParty}
                  viewer={viewer}
                  frozen={Boolean(room.terms)}
                  compact
                  milestones={room.milestones}
                />
              ) : null}

              <PaymentsPanel room={room} viewer={viewer} canManagePayouts={canManagePayouts} />

              <SharedFilesCard room={room} />
              <SharedLinksCard room={room} />
              <CancelCollabCard
                room={room}
                viewer={viewer}
                canAct={access.canAct}
                verified={access.verified}
              />

              {room.terms ? (
                <p className="px-1 pb-1 text-[11px] leading-relaxed text-ink-subtle">
                  Terms locked on {formatDate(room.terms.acceptedAt)} when the{" "}
                  {room.terms.acceptedBy} accepted.
                </p>
              ) : null}
            </div>
          </div>
        }
      />
    </div>
  );
}

function BackIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="size-5" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M12.78 5.22a.75.75 0 0 1 0 1.06L9.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z"
        clipRule="evenodd"
      />
    </svg>
  );
}
